import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { PIKKO } from "../../lib/pikko";
import PikkoLogo from "../PikkoLogo";

const links = [
  { id: "team", label: "Team" },
  { id: "poliklinik", label: "Poliklinik" },
  { id: "rawat-inap", label: "Rawat Inap" },
  { id: "grooming", label: "Grooming" },
  { id: "lokasi", label: "Lokasi" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="pikko-navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[rgba(246,241,232,0.85)] backdrop-blur-xl border-b border-[rgba(138,107,82,0.12)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          className="flex items-center gap-2.5 group"
          data-testid="navbar-logo"
        >
          <PikkoLogo size={38} />
          <div className="leading-tight">
            <div className="font-serif text-[18px] text-[var(--pikko-brown-deep)] tracking-tight font-semibold">
              PIKKO<span className="text-[var(--pikko-orange)]">.</span>
            </div>
            <div className="text-[9.5px] uppercase tracking-[0.28em] text-[var(--pikko-brown)]/75 font-medium">
              Pet Care
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className="text-sm text-[var(--pikko-brown-deep)] hover:text-[var(--pikko-orange)] transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[var(--pikko-orange)] group-hover:w-full transition-all duration-500" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={PIKKO.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-book-btn"
            className="btn-primary text-sm py-2.5 px-5"
          >
            <MessageCircle className="w-4 h-4" /> Book Appointment
          </a>
        </div>

        <button
          className="md:hidden p-2 text-[var(--pikko-brown-deep)]"
          onClick={() => setOpen((o) => !o)}
          aria-label="menu"
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="md:hidden overflow-hidden bg-[rgba(246,241,232,0.97)] backdrop-blur-xl border-t border-[rgba(138,107,82,0.12)]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.id}`}
                  className="text-[var(--pikko-brown-deep)] text-base"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={PIKKO.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="btn-primary text-sm justify-center"
                data-testid="navbar-mobile-book-btn"
              >
                <MessageCircle className="w-4 h-4" /> Book Appointment
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
